import { getExplorerGatewayApi } from '@squareup/dex-data-shared-explorer-gateway-api';
import { HTTP_METHOD } from '@squareup/dex-types-shared-http';
import { usePrefillSuccessToast } from '@squareup/dex-ui-hooks';
import {
  ApiExplorerApiResponse,
  ApiExplorerContext,
  ApiExplorerContextProvider,
  useApiExplorerAccessToken,
  useApiExplorerContext,
  useApiExplorerEndpoint,
  useApiExplorerEnvironment,
  useApiExplorerGraph,
  useApiExplorerInput,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { useTransformInputForExternal } from '@squareup/dex-utils-shared-api-explorer';
import React, { FC, PropsWithChildren, useMemo, useCallback } from 'react';

import { useTransformInputForApiRequest } from './hooks/request-runner-hooks';

const strings = {
  errors: {
    generic:
      'An unrecoverable error occurred while making your API request. Please try again',
  },
};

interface Props {}

const ApiExplorerRequestRunnerContainer: FC<PropsWithChildren<Props>> = ({
  children,
}) => {
  const contextValue = useApiExplorerContext();

  const accessToken = useApiExplorerAccessToken();
  const environment = useApiExplorerEnvironment();

  const endpoint = useApiExplorerEndpoint();
  const [apiExplorerInput] = useApiExplorerInput();
  const input = useTransformInputForExternal(apiExplorerInput, endpoint);

  const graph = useApiExplorerGraph();

  const apiRequestInput = useTransformInputForApiRequest(
    input,
    endpoint?.path,
    endpoint?.supportsFileUpload
  );

  const [makeApiRequest, apiResult] =
    getExplorerGatewayApi().useLazyApiExplorerRequestQuery();

  const { tryShowPrefillSuccessToast } = usePrefillSuccessToast();

  const apiResponse: ApiExplorerApiResponse | null = useMemo(() => {
    if (apiResult.isUninitialized) {
      return null;
    }

    // Ensures we don't show stale responses when switching pages
    if (!endpoint?.id || apiResult.originalArgs?.id !== endpoint?.id) {
      return null;
    }

    if (apiResult.isError) {
      return {
        body: strings.errors.generic,
        status: 500,
      };
    }

    return {
      body: apiResult.data?.body,
      status: apiResult.data?.status ?? undefined,
      headers: apiResult.data?.headers,
    };
  }, [
    apiResult.isUninitialized,
    apiResult.originalArgs?.id,
    apiResult.isError,
    apiResult.data?.body,
    apiResult.data?.status,
    apiResult.data?.headers,
    endpoint?.id,
  ]);

  const onApiRequestInitiated = useCallback(async () => {
    if (!endpoint) {
      return;
    }
    const response = await makeApiRequest({
      id: endpoint.id,
      applicationType: environment,
      authType: endpoint.authType === 'applicationSecret' ? 'client' : 'bearer',
      accessToken:
        endpoint.authType === 'applicationSecret'
          ? (input.authParameters['client_secret'] as string)
          : accessToken,
      version: graph.squareVersion,
      // These are equivalent but Typescript enums can't resolve each other
      method: endpoint.httpMethod.toUpperCase() as unknown as HTTP_METHOD,
      path: apiRequestInput.path,
      body: apiRequestInput.body,
      formData: apiRequestInput.formData,
    });

    tryShowPrefillSuccessToast(response.data?.status);
  }, [
    accessToken,
    apiRequestInput.body,
    apiRequestInput.formData,
    apiRequestInput.path,
    endpoint,
    environment,
    graph.squareVersion,
    input.authParameters,
    makeApiRequest,
    tryShowPrefillSuccessToast,
  ]);

  const memoizedContext: ApiExplorerContext = useMemo(() => {
    return {
      ...contextValue,
      onApiRequestInitiated,
      apiResponse,
      isApiRequestRunning: apiResult.isFetching,
    };
  }, [apiResponse, apiResult.isFetching, contextValue, onApiRequestInitiated]);

  return (
    <ApiExplorerContextProvider value={memoizedContext}>
      {children}
    </ApiExplorerContextProvider>
  );
};

export { ApiExplorerRequestRunnerContainer };
